import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const navigation = [
  { name: "Home", href: "#home", id: "home" },
  {
    name: "Sustainable Living",
    href: "#sustainable-living",
    id: "sustainable-living",
  },
  // { name: "Communities", href: "#communities", id: "communities" },
  { name: "Rivera Living", href: "#rivera-living", id: "rivera-living" },
  { name: "New Launches", href: "#launches", id: "launches" },
  { name: "Location", href: "#location", id: "location" },
  { name: "Payment Plans", href: "#payments", id: "payments" },
  // { name: "Properties", href: "#properties", id: "properties" },
  { name: "Contact", href: "#contact", id: "contact" },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [animateHeader, setAnimateHeader] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const scrollPosition = window.scrollY + 100;
      const sections = navigation.map((link) => {
        const element = document.getElementById(link.id);
        return {
          id: link.id,
          offset: element?.offsetTop || 0,
          height: element?.offsetHeight || 0,
        };
      });

      let current = "home";
      for (const section of sections) {
        if (
          scrollPosition >= section.offset &&
          scrollPosition < section.offset + section.height
        ) {
          current = section.id;
          break;
        }
      }
      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    document.documentElement.style.scrollBehavior = "smooth";

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.documentElement.style.scrollBehavior = "auto";
    };
  }, []);

  useEffect(() => {
    setAnimateHeader(true);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "py-5 bg-white shadow-lg" : "py-5 bg-transparent"
      }`}
    >
      <div className="container-width px-4 mx-auto flex items-center justify-between">
        <motion.div
          className="flex items-center"
          initial={{ opacity: 0, x: -20 }}
          animate={{
            opacity: animateHeader ? 1 : 0,
            x: animateHeader ? 0 : -20,
          }}
          transition={{ duration: 0.5 }}
        >
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-gold-400 to-gold-600 rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-200"></div>
            <img
              src="/Images/emaar-logo.png"
              alt="Emaar Logo"
              className={`h-8 relative z-10 transition-all duration-300 ${
                isScrolled ? "filter brightness-0" : ""
              }`}
            />
          </div>
        </motion.div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-8">
          {navigation.map((item, index) => (
            <motion.a
              key={item.name}
              href={item.href}
              className={`text-sm font-medium relative px-2 py-1 rounded-md transition-all ${
                activeSection === item.id
                  ? "text-gold-600 bg-silver-100/70 bg-opacity-30"
                  : isScrolled
                  ? "text-silver-600 hover:text-gold-500"
                  : "hover:text-gold-400 text-gold-200"
              }`}
              initial={{ opacity: 0, y: -10 }}
              animate={{
                opacity: animateHeader ? 1 : 0,
                y: animateHeader ? 0 : -10,
              }}
              transition={{ duration: 0.3, delay: 0.1 + index * 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              {item.name}
              {activeSection === item.id && (
                <motion.span
                  className="absolute bottom-0 left-0 w-full h-0.5"
                  layoutId="activeIndicator"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
            </motion.a>
          ))}
        </nav>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{
            opacity: animateHeader ? 1 : 0,
            x: animateHeader ? 0 : 20,
          }}
          transition={{ duration: 0.5 }}
        >
          <a
            href="#contact"
            className={`hidden md:inline-block px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 relative overflow-hidden group ${
              isScrolled
                ? "bg-gradient-to-r from-gold-500 to-gold-600 text-white"
                : "bg-gradient-to-r from-gold-500 to-gold-600 text-white"
            }`}
          >
            <span className="relative z-10">Contact Us</span>
            <span className="absolute inset-0 bg-gradient-to-r from-gold-600 to-gold-400 opacity-0 group-hover:opacity-100 transition duration-300"></span>
          </a>

          {/* Mobile menu button */}
          <button
            className="md:hidden relative z-50 p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <div className="w-6 flex flex-col items-end justify-center gap-1.5">
              <span
                className={`block h-0.5 transform transition duration-300 ease-in-out ${
                  mobileMenuOpen
                    ? "w-6 rotate-45 translate-y-2"
                    : `w-6 ${isScrolled ? "bg-silver-600" : "bg-white"}`
                }`}
              ></span>
              <span
                className={`block h-0.5 transition duration-300 ease-in-out ${
                  mobileMenuOpen
                    ? "w-0 opacity-0"
                    : `w-4 ${isScrolled ? "bg-silver-600" : "bg-white"}`
                }`}
              ></span>
              <span
                className={`block h-0.5 transform transition duration-300 ease-in-out ${
                  mobileMenuOpen
                    ? "w-6 -rotate-45 -translate-y-2 bg-white"
                    : `w-5 ${isScrolled ? "bg-silver-600" : "bg-white"}`
                }`}
              ></span>
            </div>
          </button>
        </motion.div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 bg-gradient-to-br from-gold-900 to-silver-900 z-40 flex flex-col items-center justify-center p-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex flex-col items-center space-y-8">
              {navigation.map((item, index) => (
                <motion.a
                  key={item.name}
                  href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-2xl font-medium relative ${
                    activeSection === item.id
                      ? "text-gold-300"
                      : "text-midnightBlack hover:text-gold-400"
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  exit={{ opacity: 0, y: 10 }}
                >
                  {item.name}
                  {activeSection === item.id && (
                    <motion.span
                      className="absolute -bottom-2 left-0 w-full h-1 bg-gold-300 rounded-full"
                      layoutId="mobileActiveIndicator"
                    />
                  )}
                </motion.a>
              ))}
              <motion.a
                href="#contact"
                onClick={() => setMobileMenuOpen(false)}
                className="mt-8 px-6 py-3 rounded-full text-lg font-medium bg-gradient-to-r from-gold-500 to-gold-600 text-white hover:from-gold-600 hover:to-gold-700 transition-all"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                exit={{ opacity: 0, y: 10 }}
              >
                Contact Us
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
